import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, StarOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { scrollToElement } from './gsap';
import './landing.css';

type NavItem = { key: string; label: string; to?: string; section?: string };

/**
 * Header flottant commun aux pages publiques (landing, how-it-works, why, blog).
 * Rendu hors du ScrollSmoother (position: fixed).
 */
const PublicHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Ferme le menu mobile à chaque navigation
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const items: NavItem[] = [
    { key: 'features', label: t.nav.features, section: 'features' },
    { key: 'pricing', label: t.footer.pricing, section: 'pricing' },
    { key: 'how', label: t.nav.howItWorks, to: '/how-it-works' },
    { key: 'why', label: t.nav.why, to: '/why' },
    { key: 'blog', label: t.nav.blog, to: '/blog' },
  ];

  const goToSection = (id: string) => {
    setOpen(false);
    if (location.pathname === '/') {
      scrollToElement(id);
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  };

  const isActive = (item: NavItem) =>
    !!item.to && (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  const cta = isAuthenticated ? (
    <Button className="ts-header-cta ts-header-cta--ghost" onClick={() => navigate('/app/dashboard')}>
      {t.nav.dashboard}
    </Button>
  ) : (
    <Button type="primary" className="ts-header-cta ts-header-cta--primary" onClick={() => navigate('/login')}>
      {t.nav.getStarted}
      <ArrowRightOutlined />
    </Button>
  );

  return (
    <header className={`metamask-header ts-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="ts-header-wrapper">
        <div className="ts-header-logo" onClick={() => navigate('/')} role="link" aria-label="Home">
          <img
            src="/images/logo.png"
            alt="Telegram Stars Payment Platform"
            className="ts-header-logo-img"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.add('show');
            }}
          />
          <StarOutlined className="ts-header-logo-fallback" />
        </div>

        <nav className="ts-nav" aria-label="Main">
          {items.map((item) =>
            item.to ? (
              <Link key={item.key} to={item.to} className={`ts-nav-link ${isActive(item) ? 'active' : ''}`}>
                {item.label}
              </Link>
            ) : (
              <button key={item.key} type="button" className="ts-nav-link" onClick={() => goToSection(item.section!)}>
                {item.label}
              </button>
            ),
          )}
        </nav>

        <div className="ts-header-actions">
          <LanguageSwitcher />
          {cta}
          <button
            type="button"
            className={`ts-burger ${open ? 'open' : ''}`}
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {open && (
        <div className="ts-mobile-menu">
          {items.map((item, i) => (
            <button
              key={item.key}
              type="button"
              className={`ts-mobile-item ${isActive(item) ? 'active' : ''}`}
              style={{ ['--i' as string]: i }}
              onClick={() => (item.to ? navigate(item.to) : goToSection(item.section!))}
            >
              {item.label}
              <ArrowRightOutlined />
            </button>
          ))}
          {isAuthenticated ? (
            <Button className="ts-header-cta ts-header-cta--ghost ts-mobile-cta" onClick={() => navigate('/app/dashboard')}>
              {t.nav.dashboard}
            </Button>
          ) : (
            <Button type="primary" className="ts-header-cta ts-header-cta--primary ts-mobile-cta" onClick={() => navigate('/login')}>
              {t.nav.getStarted}
            </Button>
          )}
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
